import React, { useEffect, useRef, useState, useCallback } from "react";

const VISIBLE = 7;
const HALF    = Math.floor(VISIBLE / 2); // 3

export default function TrustedBySection({ registerIntent, url }) {

  const PARTNERS = [
    { id: 1,  name: "Omnix AI",         logo: url + "images/new-home/partners/omnix.png"      },
    { id: 2,  name: "Concord",           logo: url + "images/new-home/partners/concord.png"    },
    { id: 3,  name: "Brainbox",          logo: url + "images/new-home/partners/brainbox.png"   },
    { id: 4,  name: "Embark\nAthletics",  logo: url + "images/new-home/partners/embark.png"     },
    { id: 5,  name: "EVgo",              logo: url + "images/new-home/partners/evgo.png"        },
    { id: 6,  name: "Fauji Foods",       logo: url + "images/new-home/partners/ffl.png"         },
    { id: 7,  name: "Fidelity",          logo: url + "images/new-home/partners/fidelity.png"   },
    { id: 8,  name: "Intelihire",        logo: url + "images/new-home/partners/intelihire.png" },
    { id: 9,  name: "Mairotec",          logo: url + "images/new-home/partners/mairotec.png"   },
    { id: 10, name: "Movig",             logo: url + "images/new-home/partners/movig.png"       },
    { id: 11, name: "Pepsico",           logo: url + "images/new-home/partners/pepsico.png"    },
    { id: 12, name: "PSO",               logo: url + "images/new-home/partners/pso.png"         },
    { id: 13, name: "Qarshi",            logo: url + "images/new-home/partners/qarshi.png"     },
    { id: 14, name: "SBM",               logo: url + "images/new-home/partners/sbm.png"         },
    { id: 15, name: "SNGPL",             logo: url + "images/new-home/partners/sngpl.png"       },
    { id: 16, name: "Talygen",           logo: url + "images/new-home/partners/talygen.png"    },
    { id: 17, name: "Zones",             logo: url + "images/new-home/partners/zones.png"       },
    { id: 18, name: "Zorbis",            logo: url + "images/new-home/partners/zorbis.png"      },
  ];

  const n = PARTNERS.length;

  const [active,  setActive]  = useState(0);
  const [logoKey, setLogoKey] = useState(0);
  const activeRef             = useRef(0);
  const touchStart            = useRef(0);

  // ── goTo: move wheel by exactly 1 step, no debounce here ──────────────
  // Debounce is handled by ScrollyStage's own lock — we trust it
  const goTo = useCallback((raw) => {
    const next = ((raw % n) + n) % n;
    if (next === activeRef.current) return;
    activeRef.current = next;
    setActive(next);
    setLogoKey(k => k + 1);
  }, [n]);

  // ── registerIntent: single source of truth for all scroll ─────────────
  // Returns true  → consumed this tick (stay in section)
  // Returns false → pass to layout (leave section)
  useEffect(() => {
    if (!registerIntent) return;
    registerIntent((dir) => {
      const next = activeRef.current + dir;
      // Still within list → consume
      if (next >= 0 && next < n) {
        goTo(next);
        return true;
      }
      // Out of bounds → leave section
      return false;
    });
  }, [registerIntent, goTo, n]);

  // ── Touch support (mobile swipe) ───────────────────────────────────────
  // NOTE: we do NOT add a wheel listener here — that's handled by ScrollyStage
  // Touch is separate and fine to handle locally
  const handleTouchStart = useCallback((e) => {
    touchStart.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    const dy = touchStart.current - e.changedTouches[0].clientY;
    if (Math.abs(dy) < 30) return;
    const dir  = dy > 0 ? 1 : -1;
    const next = activeRef.current + dir;
    if (next >= 0 && next < n) {
      goTo(next);
    }
    // If out of bounds on touch we just stop — layout handles section change
  }, [goTo, n]);

  const partner = PARTNERS[active];

  const CLOCK_RADIUS = 180;
  const ANGLE_STEP   = 22;

  const items = Array.from({ length: VISIBLE }, (_, slot) => {
    const offset   = slot - HALF;
    const idx      = ((active + offset) % n + n) % n;
    const isActive = offset === 0;
    const absO     = Math.abs(offset);

    const angleDeg = offset * ANGLE_STEP;
    const angleRad = (angleDeg * Math.PI) / 180;

    const x = CLOCK_RADIUS * Math.cos(angleRad);
    const y = CLOCK_RADIUS * Math.sin(angleRad);

    const opacity = isActive ? 1 : Math.max(0.15, 0.75 - absO * 0.22);
    const scale   = isActive ? 1 : Math.max(0.6,  0.85 - absO * 0.1);

    return { idx, offset, isActive, absO, x, y, angleDeg, opacity, scale,
             name: PARTNERS[idx].name };
  });

  return (
    <section
      id="tech-section"
      className="tb"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      // ── NO onWheel here — wheel is handled exclusively by ScrollyStage ──
    >
      <div className="tb__grid" />
      <div className="tb__glow" />

      {/* ── LEFT: arc wheel ── */}
      <div className="tb__wheel-col">
        <div className="tb__arc">
          {items.map(({ idx, offset, isActive, absO, x, y, angleDeg,
                        opacity, scale, name }) => (
            <button
              key={`${idx}-${offset}`}
              className={`tb__name ${isActive ? "tb__name--active" : ""}`}
              onClick={() => goTo(idx)}
              style={{
                transform:            `translate(${x}px, calc(-50% + ${y}px)) rotate(${angleDeg}deg) scale(${scale})`,
                WebkitTransform:      `translate(${x}px, calc(-50% + ${y}px)) rotate(${angleDeg}deg) scale(${scale})`,
                transformOrigin:      "left center",
                WebkitTransformOrigin:"left center",
                opacity,
                zIndex: isActive ? 10 : HALF - absO,
                left:   "0",
                top:    "50%",
              }}
            >
              {name}
            </button>
          ))}
        </div>
      </div>
      

      {/* ── CENTER: logo ── */}
      <div className="tb__logo-col">
        <div className="tb__logo-wrap" key={logoKey}>
          {partner.logo
            ? <img src={partner.logo} alt={partner.name} className="tb__logo-img" />
            : <span className="tb__logo-name">{partner.name}</span>
          }
        </div>
      </div>

      {/* ── RIGHT: tagline + dots ── */}
      <div className="tb__info-col">
        <div className="tb__info-bar" />
        <p className="tb__tagline">
          Chosen by brands that<br />
          value creativity,<br />
          technology, and growth.
        </p>
        <div className="tb__dots">
          {PARTNERS.map((_, i) => (
            <button
              key={i}
              className={`tb__dot ${i === active ? "tb__dot--active" : ""}`}
              onClick={() => goTo(i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}


// import React, { useEffect, useRef, useState, useCallback } from "react";


// // How many names visible at once (must be odd)
// const VISIBLE = 7;  // 1 active + 2 above + 2 below
// const HALF    = Math.floor(VISIBLE / 2); // 2
 
// export default function TrustedBySection({ registerIntent, url }) {

//   const PARTNERS = [
//     { id: 1, name: "Omnix AI",         logo: url + "images/new-home/partners/omnix.png" },
//     { id: 2, name: "Concord",          logo: url + "images/new-home/partners/concord.png" },
//     { id: 3, name: "Brainbox",         logo: url + "images/new-home/partners/brainbox.png" },
//     { id: 5, name: "Embark Athletics", logo: url + "images/new-home/partners/embark.png" },
//     { id: 6, name: "EVgo",        logo: url + "images/new-home/partners/evgo.png" },
//     { id: 5, name: "Fauji Foods", logo: url + "images/new-home/partners/ffl.png" },
//     { id: 5, name: "Fidelity",    logo: url + "images/new-home/partners/fidelity.png" },
//     { id: 4, name: "Intelihire",  logo: url + "images/new-home/partners/intelihire.png" },
//     { id: 4, name: "Mairotec",    logo: url + "images/new-home/partners/mairotec.png" },
//     { id: 4, name: "Movig",       logo: url + "images/new-home/partners/movig.png" },
//     { id: 4, name: "Pepsico",     logo: url + "images/new-home/partners/pepsico.png" },
//     { id: 4, name: "PSO",         logo: url + "images/new-home/partners/pso.png" },
//     { id: 4, name: "Qarshi",      logo: url + "images/new-home/partners/qarshi.png" },
//     { id: 7, name: "SBM",         logo: url + "images/new-home/partners/sbm.png" },
//     { id: 7, name: "SNGPL",       logo: url + "images/new-home/partners/sngpl.png" },
//     { id: 7, name: "Talygen",     logo: url + "images/new-home/partners/talygen.png" },
//     { id: 8, name: "Zones",       logo: url + "images/new-home/partners/zones.png" },
//     { id: 9, name: "Zorbis",       logo: url + "images/new-home/partners/zorbis.png" },
//   ];
   

//   const [active,  setActive]  = useState(0);
//   const [logoKey, setLogoKey] = useState(0);
//   const activeRef             = useRef(0);
//   const autoRef               = useRef(null);
//   const debounce              = useRef(false);
//   const touchStart            = useRef(0);
 
//   const n = PARTNERS.length;
 
//   const goTo = useCallback((raw) => {
//     const next = ((raw % n) + n) % n;
//     if (next === activeRef.current) return;
//     activeRef.current = next;
//     setActive(next);
//     setLogoKey(k => k + 1);
//   }, [n]);
 
//   const startAuto = useCallback(() => {
//     // Auto-play disabled
//   }, []);
 
//   useEffect(() => {
//     if (!registerIntent) return;
//     registerIntent((dir) => {
//       const next = activeRef.current + dir;
//       if (next >= 0 && next < n) {
//         clearInterval(autoRef.current);
//         goTo(next);
//         startAuto();
//         return true;
//       }
//       return false;
//     });
//   }, [registerIntent, goTo, startAuto, n]);
 
// //   const handleWheel = useCallback((e) => {
// //     e.preventDefault();
// //     if (debounce.current) return;
// //     debounce.current = true;
// //     setTimeout(() => { debounce.current = false; }, 600);
// //     clearInterval(autoRef.current);
// //     goTo(activeRef.current + (e.deltaY > 0 ? 1 : -1));
// //     startAuto();
// //   }, [goTo, startAuto]);

// const handleWheel = useCallback((e) => {
//   if (debounce.current) return;

//   debounce.current = true;

//   setTimeout(() => {
//     debounce.current = false;
//   }, 600);

//   clearInterval(autoRef.current);

//   goTo(activeRef.current + (e.deltaY > 0 ? 1 : -1));

//   startAuto();
// }, [goTo, startAuto]);
 
//   const handleTouchStart = useCallback((e) => { touchStart.current = e.touches[0].clientY; }, []);
//   const handleTouchEnd   = useCallback((e) => {
//     const dy = touchStart.current - e.changedTouches[0].clientY;
//     if (Math.abs(dy) < 24) return;
//     clearInterval(autoRef.current);
//     goTo(activeRef.current + (dy > 0 ? 1 : -1));
//     startAuto();
//   }, [goTo, startAuto]);
 
//   const partner = PARTNERS[active];
 
//   /**
//    * CLOCK-FACE ARC MATH
//    * --------------------
//    * All names radiate from a single PIVOT point on the far left.
//    * Active = 3 o'clock (0°), above = 1-2 o'clock (negative angles), below = 4-5 o'clock (positive).
//    *
//    * Each name's START POINT (left edge of text) sits on a circle of RADIUS px.
//    * The pivot is at (0, 50%) of the container — far left, vertically centered.
//    *
//    * Position of name's left edge on the circle:
//    *   x = RADIUS * cos(angle)
//    *   y = RADIUS * sin(angle)   (positive = down)
//    *
//    * The name also ROTATES by the same angle so it points outward from the pivot,
//    * exactly like clock hands.
//    */
//   const CLOCK_RADIUS  = 180;   // distance from pivot to where text starts
//   const ANGLE_STEP    = 22;    // degrees between each name (clock positions)
 
//   const items = Array.from({ length: VISIBLE }, (_, slot) => {
//     const offset   = slot - HALF;
//     const idx      = ((active + offset) % n + n) % n;
//     const isActive = offset === 0;
//     const absO     = Math.abs(offset);
 
//     const angleDeg = offset * ANGLE_STEP;            // 0° = active, ±20°, ±40°
//     const angleRad = (angleDeg * Math.PI) / 180;
 
//     // Position where text LEFT EDGE starts (relative to pivot at left-center)
//     const x = CLOCK_RADIUS * Math.cos(angleRad);    // rightward from pivot
//     const y = CLOCK_RADIUS * Math.sin(angleRad);    // downward from pivot
 
//     const opacity = isActive ? 1 : Math.max(0.15, 0.75 - absO * 0.22);
//     const scale   = isActive ? 1 : Math.max(0.6,  0.85 - absO * 0.1);
 
//     return { idx, offset, isActive, absO, x, y, angleDeg, opacity, scale, name: PARTNERS[idx].name };
//   });
 
//   return (
//     <section
//       id="tech-section"
//       className="tb"
//       onWheel={handleWheel}
//       onTouchStart={handleTouchStart}
//       onTouchEnd={handleTouchEnd}
//     >
//       {/* Subtle dot grid */}
//       <div className="tb__grid" />
//       {/* Orange glow at wheel center */}
//       <div className="tb__glow" />
 
//       {/* ── LEFT: arc wheel ── */}
//       <div className="tb__wheel-col">
 
//         {/* <div className="tb__top-label">
//           <span className="tb__pip" />
//           <span>Our Partners</span>
//         </div> */}
 
//         {/* The arc container — names are absolutely placed via transform */}
//         <div className="tb__arc">
//           {/* Faint accent line through active slot */}
//           {/* <div className="tb__arc-line" /> */}
 
//         {items.map(({ idx, offset, isActive, absO, x, y, angleDeg, opacity, scale, name }) => {
//             return (
//                 <button
//                 key={`${idx}-${offset}`}
//                 className={`tb__name ${isActive ? "tb__name--active" : ""}`}
//                 onClick={() => { clearInterval(autoRef.current); goTo(idx); startAuto(); }}
//                 style={{
//                     transform: `translate(${x}px, calc(-50% + ${y}px)) rotate(${angleDeg}deg) scale(${scale})`,
//                     WebkitTransform: `translate(${x}px, calc(-50% + ${y}px)) rotate(${angleDeg}deg) scale(${scale})`,
//                     transformOrigin: "left center",
//                     WebkitTransformOrigin: "left center",
//                     opacity,
//                     zIndex: isActive ? 10 : HALF - absO,
//                     left: "0",
//                     top: "50%",
//                 }}
//                 >
//                 {name}
//                 </button>
//             );
//             })}
//         </div>
 
//         {/* Counter */}
//         {/* <div className="tb__counter">
//           <span className="tb__cnt-cur" >{String(active + 1).padStart(2,"0")}</span>
//           <span className="tb__cnt-line" />
//           <span className="tb__cnt-tot">{String(n).padStart(2,"0")}</span>
//         </div> */}
//       </div>
 
//       {/* ── CENTER: logo ── */}
//       <div className="tb__logo-col">
//         <div className="tb__logo-wrap" key={logoKey}>
//           {partner.logo
//             ? <img src={partner.logo} alt={partner.name} className="tb__logo-img" />
//             : <span className="tb__logo-name">{partner.name}</span>
//           }
//         </div>
//       </div>
 
//       {/* ── RIGHT: tagline + dots ── */}
//       <div className="tb__info-col">
//         <div className="tb__info-bar" />
//         <p className="tb__tagline">
//           Chosen by brands that<br />
//           value creativity,<br />
//           technology, and growth.
//         </p>
//         <div className="tb__dots">
//           {PARTNERS.map((_, i) => (
//             <button
//               key={i}
//               className={`tb__dot ${i === active ? "tb__dot--active" : ""}`}
//               onClick={() => { clearInterval(autoRef.current); goTo(i); startAuto(); }}
//             />
//           ))}
//         </div>
//       </div>
 
//       {/* Nav arrows */}
//       {/* <div className="tb__nav">
//         <button className="tb__nav-btn" onClick={() => { clearInterval(autoRef.current); goTo(active - 1); startAuto(); }}>
//           <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
//             <path d="M7 1 3 5l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
//           </svg>
//         </button>
//         <button className="tb__nav-btn" onClick={() => { clearInterval(autoRef.current); goTo(active + 1); startAuto(); }}>
//           <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
//             <path d="M3 1l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
//           </svg>
//         </button>
//       </div> */}
//     </section>
//   );
// }
 